/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, Input, Signal, signal, ViewEncapsulation } from '@angular/core';
import {
    AttainmentGroupNode,
    AttainmentNode,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    DegreeProgramme,
    GroupingModule,
    OtmId,
    StudyModule,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import {
    isAttainmentGroupNode,
    isAttainmentReferenceNode,
    isCourseUnitAttainment,
    isCustomCourseUnitAttainment,
    isCustomModuleAttainment,
    isModuleAttainment,
} from '../../attainment/AttainmentUtil';
import { getAttainmentFromPlanData } from '../../service/attainment-in-plan-data';
import { PlanStructureContext } from '../plan-structure-context';

interface NodeData {
    attainmentGroupNodes: AttainmentGroupNode[];
    courseUnitAttainmentCourseUnitIds: OtmId[];
    moduleAttainmentModuleIds: OtmId[];
    customCourseUnitAttainmentIds: OtmId[];
    customModuleAttainmentIds: OtmId[];
}

@Component({
    selector: 'sis-plan-structure-attainment-node-structure',
    templateUrl: './plan-structure-attainment-node-structure.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureAttainmentNodeStructureComponent {
    @Input({ required: true }) set attainmentNodes(nodes: AttainmentNode[]) {
        this._attainmentNodes.set(nodes);
    }

    _attainmentNodes = signal([], { equal: _.isEqual });
    @Input({ required: true }) headingLevel: number;

    protected readonly planStructureContext = inject(PlanStructureContext);

    filteredNodes: Signal<NodeData> = computed(() => {
        const attainmentGroupNodes: AttainmentGroupNode[] = [];
        const courseUnitAttainmentCourseUnitIds: OtmId[] = [];
        const moduleAttainmentModuleIds: OtmId[] = [];
        const customCourseUnitAttainmentIds: OtmId[] = [];
        const customModuleAttainmentIds: OtmId[] = [];

        for (const node of this._attainmentNodes() ?? []) {
            if (isAttainmentGroupNode(node)) {
                attainmentGroupNodes.push(node);
            }
            if (isAttainmentReferenceNode(node)) {
                const attainment = this.planStructureContext.planData().attainmentsById[node.attainmentId];

                if (isCourseUnitAttainment(attainment)) {
                    courseUnitAttainmentCourseUnitIds.push(attainment.courseUnitId);
                }
                if (isModuleAttainment(attainment)) {
                    moduleAttainmentModuleIds.push(attainment.moduleId);
                }
                if (isCustomCourseUnitAttainment(attainment)) {
                    customCourseUnitAttainmentIds.push(attainment.id);
                }
                if (isCustomModuleAttainment(attainment)) {
                    customModuleAttainmentIds.push(attainment.id);
                }
            }
        }
        return {
            attainmentGroupNodes,
            courseUnitAttainmentCourseUnitIds,
            moduleAttainmentModuleIds,
            customCourseUnitAttainmentIds,
            customModuleAttainmentIds,
        };
    });

    getCustomCourseUnitAttainment(id: OtmId): CustomCourseUnitAttainment | undefined {
        return getAttainmentFromPlanData(this.planStructureContext.planData(), id, isCustomCourseUnitAttainment);
    }

    getCustomModuleAttainment(id: OtmId): CustomModuleAttainment | undefined {
        return getAttainmentFromPlanData(this.planStructureContext.planData(), id, isCustomModuleAttainment);
    }

    getModule(id: OtmId): StudyModule | DegreeProgramme | GroupingModule {
        return this.planStructureContext.planData().modulesById[id] as StudyModule | DegreeProgramme | GroupingModule;
    }
}
